import { computed } from 'vue';
import { store } from '@/plugins/store';
import type { MedicineInventoryScope } from '@/types';

/**
 * The period and farm scope every inventory surface reads: the global period picker's dates and the
 * farms ticked in the global location picker. Stock is kept per farm, so barn, batch and section
 * selections do not narrow it and pig type does not apply.
 */
export function useInventoryScope() {
    const scope = computed<MedicineInventoryScope>(() => ({
        period: store.getters.getSelectedPeriodByDate,
        locationIds: [...store.state.selectedFarmIds],
    }));
    const isScopeEnabled = computed(() => scope.value.locationIds.length > 0);

    /** The period picker's dates narrowed to one farm: what a ledger key's own pages (detail and its movements) read. */
    const scopeOfFarm = (farmId: number): MedicineInventoryScope => ({ period: scope.value.period, locationIds: [farmId] });

    /** The period's first and last day, "yyyy-MM-dd", read back from the query string the endpoints take. */
    const periodRange = computed(() => {
        const params = new URLSearchParams(scope.value.period);

        return { from: params.get('from') ?? '', to: params.get('to') ?? '' };
    });

    /** The farms stock can be written to (bought, spilled, counted): those the reader has ReadWrite on. */
    const writableFarms = computed(() => store.state.farms.filter((farm) => farm.permission.key === 'ReadWrite'));

    /** The farms ticked in the location picker, in the order of the farm list. */
    const selectedFarms = computed(() => store.state.farms.filter((farm) => store.state.selectedFarmIds.includes(farm.farm_id)));

    return { scope, isScopeEnabled, scopeOfFarm, periodRange, writableFarms, selectedFarms };
}
